import { motion } from "framer-motion";
import { Star, Quote, TrendingUp, Clock, Target } from "lucide-react";

const highlights = [
  { icon: Clock, value: "80%", label: "Screening Time Saved", color: "text-accent bg-accent/10" },
  { icon: TrendingUp, value: "65%", label: "Faster Time-to-Hire", color: "text-emerald-500 bg-emerald-500/10" },
  { icon: Target, value: "94%", label: "Matching Accuracy", color: "text-blue-500 bg-blue-500/10" },
];

const testimonials = [
  {
    quote: "Intellr. cut our screening time by 73%. The AI voice interviews are incredibly natural and candidates genuinely enjoy the experience. We went from 42 days to 14 days average time-to-hire across all engineering roles.",
    name: "Head of Talent Acquisition",
    role: "Large Fintech Org, Mumbai",
    avatar: "TA",
    metric: "73% time saved",
  },
  {
    quote: "The automation engine alone freed up 28 hours per week per recruiter. Our team now focuses on relationship building and closing instead of resume screening. The ROI was visible within the first month of deployment.",
    name: "VP People Operations",
    role: "Fortune 500 SaaS, Bangalore",
    avatar: "VP",
    metric: "28h/week saved",
  },
  {
    quote: "As a startup scaling post-Series B, every hire matters. Intellr.'s multi-dimensional scoring surfaces candidates who actually thrive in our culture - not just those with the right keywords. Quality-of-hire improved by 52%.",
    name: "Co-Founder & CHRO",
    role: "Edtech Unicorn, Delhi NCR",
    avatar: "CH",
    metric: "52% quality ↑",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const SocialProofSection = () => {
  return (
    <section className="py-32 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-[20%] w-[600px] h-[600px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-4 border border-slate-200"
          >
            Success Stories
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
          >
            Real Results from <br /> <span className="text-accent underline decoration-accent/20 underline-offset-8">Real Teams</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-500 max-w-xl mx-auto font-medium"
          >
            Join hundreds of forward-thinking companies hiring smarter with AI.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-16"
        >
          {highlights.map((h) => (
            <div key={h.label} className="rounded-2xl bg-white border border-slate-100 p-5 text-center shadow-sm">
              <div className={`w-10 h-10 rounded-xl ${h.color} flex items-center justify-center mx-auto mb-3`}>
                <h.icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{h.value}</p>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1">{h.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto"
        >
          {testimonials.map((t) => (
            <motion.div
              key={t.name}
              variants={item}
              whileHover={{ y: -8 }}
              className="relative group rounded-3xl p-8 bg-white border border-slate-100 hover:border-accent/20 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <Quote className="absolute top-6 right-8 w-10 h-10 text-slate-100 group-hover:text-accent/5 transition-colors" />

              <div className="flex items-center gap-3 mb-6">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-accent text-accent" />
                  ))}
                </div>
                <span className="text-[10px] font-bold text-accent bg-accent/5 px-2 py-0.5 rounded-full border border-accent/10">
                  {t.metric}
                </span>
              </div>

              <p className="text-base text-slate-700 leading-relaxed mb-8 font-medium">
                &ldquo;{t.quote}&rdquo;
              </p>

              <div className="flex items-center gap-4 border-t border-slate-50 pt-6">
                <div className="w-12 h-12 rounded-full bg-slate-100 text-accent font-bold text-sm flex items-center justify-center shadow-sm">
                  {t.avatar}
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm tracking-tight">{t.name}</p>
                  <p className="text-xs text-slate-500 font-medium">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProofSection;
